import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQueryWithReauth } from 'src/apis/baseQuery';
import {
  DataDTOPagedDataDataDTOStoreListing,
  DataDTOStore,
  DataDTOStoreCrud,
  DataDTOTimezoneInfo,
} from 'src/types/api/generated/api';
import { PaginationParams } from 'src/types/common';

export const storeAPI = createApi({
  reducerPath: 'storeAPI',
  baseQuery: axiosBaseQueryWithReauth({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ['Store'],
  endpoints: (builder) => ({
    getStores: builder.query<
      DataDTOPagedDataDataDTOStoreListing,
      PaginationParams & { clientId: number }
    >({
      query: ({ page, pageSize, orderBy, filters, searchTerm, clientId }) => ({
        url: 'Stores',
        method: 'GET',
        params: {
          page,
          pageSize,
          orderBy,
          searchTerm,
          filters: filters ? JSON.stringify(filters) : undefined,
          clientId,
        },
      }),
      providesTags: [{ type: 'Store', id: 'LIST' }],
    }),

    getStoreById: builder.query<DataDTOStore, { id: number }>({
      query: ({ id }) => ({
        url: `Stores/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Store', id }],
    }),

    createStore: builder.mutation<DataDTOStore, DataDTOStoreCrud>({
      query: (body) => ({
        url: 'Stores',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Store', id: 'LIST' }],
    }),

    updateStore: builder.mutation<DataDTOStore, { id: number; body: DataDTOStoreCrud }>({
      query: ({ id, body }) => ({
        url: `Stores/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Store', id: 'LIST' },
        { type: 'Store', id },
      ],
    }),

    deleteStore: builder.mutation<boolean, { id: number }>({
      query: ({ id }) => ({
        url: `Stores/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Store', id: 'LIST' }],
    }),

    getTimezones: builder.query<DataDTOTimezoneInfo[], void>({
      query: () => ({
        url: 'Metadata/timezoneInfo',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetStoresQuery,
  useLazyGetStoresQuery,
  useGetStoreByIdQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useDeleteStoreMutation,
  useGetTimezonesQuery,
} = storeAPI;
